import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation, useRoute } from "wouter";
import { RoleEditor, type PermissionGroup, type RoleEditorValues, type RoleRow } from "@/components/admin/users/role-editor";
import { PageHeader } from "@/components/shared/page-header";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { getQueryFn, apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Trash2 } from "lucide-react";

export default function RoleDetailPage() {
  const { toast } = useToast();
  const [location, navigate] = useLocation();
  const [, params] = useRoute("/users/roles/:id");
  const roleId = params?.id;
  const isCreating = location === "/users/roles/new";

  const { data: permissionGroups, isLoading: permissionsLoading } = useQuery<PermissionGroup[]>({
    queryKey: ["/api/permissions"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const {
    data: role,
    isLoading: roleLoading,
  } = useQuery<RoleRow>({
    queryKey: [`/api/roles/${roleId}`],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: Boolean(roleId) && !isCreating,
  });

  const saveRole = useMutation({
    mutationFn: async (payload: RoleEditorValues) => {
      if (roleId && !isCreating) {
        const response = await apiRequest("PATCH", `/api/roles/${roleId}`, payload);
        return (await response.json()) as RoleRow;
      }

      const response = await apiRequest("POST", "/api/roles", payload);
      return (await response.json()) as RoleRow;
    },
    onSuccess: async (savedRole) => {
      await queryClient.invalidateQueries({ queryKey: ["/api/roles"] });
      await queryClient.invalidateQueries({ queryKey: [`/api/roles/${savedRole.id}`] });
      toast({ title: isCreating ? "Role created" : "Role updated" });
      navigate(`/users/roles/${savedRole.id}`);
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to save role",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteRole = useMutation({
    mutationFn: async () => {
      if (!roleId) return;
      await apiRequest("DELETE", `/api/roles/${roleId}`);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/roles"] });
      toast({ title: "Role deleted" });
      navigate("/users");
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to delete role",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const groups = Array.isArray(permissionGroups) ? permissionGroups : [];
  const isLoading = permissionsLoading || (!isCreating && roleLoading);

  return (
    <div className="space-y-6">
      <PageHeader
        title={isCreating ? "Create Role" : role?.name ?? "Role Detail"}
        description={
          isCreating
            ? "Create a role in a full-page editor so larger permission sets stay manageable."
            : "Review and adjust this role's permission set in a dedicated detail view."
        }
        action={
          <div className="flex gap-2">
            {!isCreating && role && !role.isSystem ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => deleteRole.mutate()}
                disabled={deleteRole.isPending}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </Button>
            ) : null}
            <Button type="button" variant="outline" onClick={() => navigate("/users")}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to users
            </Button>
          </div>
        }
      />

      {isLoading ? (
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
          <div className="space-y-6">
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-[640px] w-full" />
          </div>
          <Skeleton className="h-56 w-full" />
        </div>
      ) : (
        <RoleEditor
          groups={groups}
          defaultValues={
            role
              ? {
                  name: role.name,
                  description: role.description ?? "",
                  audience: role.audience,
                  permissions: role.permissions,
                }
              : undefined
          }
          audienceDisabled={role?.isSystem}
          isSystemRole={role?.isSystem}
          roleSlug={role?.slug ?? null}
          isPending={saveRole.isPending}
          submitLabel={isCreating ? "Create role" : "Save role"}
          onSubmit={(values) => saveRole.mutate(values)}
        />
      )}
    </div>
  );
}
